import { useEffect } from 'react'

import { z } from 'zod'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormDescription,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  NumberInput,
  SearchSelect,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { ALLOCATION_RULES, POSITION_COMPONENT_TYPES } from '@/pages/operations/pnl-attributions/constants'
import { useAddToPositionMutation } from '@/pages/operations/pnl-attributions/mutations'

export interface AddToPositionDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    fund_name: string
    value_date: string
    instrument_id: number
    unique_identifier: string
    batch_id?: number | null
  }
}

const addToPositionSchema = z.object({
  component_type: z.enum(Object.values(POSITION_COMPONENT_TYPES)),
  total_amount_base: z.coerce.number(),
  reason: z.string().min(1, 'You must provide a reason for adding a component.'),
  source_reference: z.string().optional().nullable(),
  allocation_rule: z.enum(Object.values(ALLOCATION_RULES)),
})

export const AddToPositionDialog: React.FC<AddToPositionDialogProps> = ({ mutationParams, ...props }) => {
  const addToPositionMutation = useAddToPositionMutation({
    fund_name: mutationParams.fund_name,
    value_date: mutationParams.value_date,
  })

  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: {
        allocation_rule: ALLOCATION_RULES.SIGNED_QUANTITY_NOTIONAL_PRO_RATA,
      },
    },
    addToPositionSchema,
  )

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await addToPositionMutation.mutateAsync({
      ...data,
      fund_name: mutationParams.fund_name,
      value_date: mutationParams.value_date,
      instrument_id: mutationParams.instrument_id,
      batch_id: mutationParams.batch_id,
    })
    props.onClose?.()
  })

  return (
    <ConfirmationDialog
      {...props}
      action={onSubmit}
      title={`Add component to ${mutationParams.unique_identifier}`}
      description={
        'This will add a new P&L component to the position. The P&L of the new component will be allocated to each trade in the position, as per the selected allocation rule.'
      }
      valid={form.formState.isValid}
      pending={addToPositionMutation.isPending}
    >
      <Form {...form}>
        <div className="space-y-4">
          <FormFieldRow>
            <FormField
              control={form.control}
              name="component_type"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Component Type</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      value={field.value}
                      setValue={(val) => field.onChange(val)}
                      placeholder="Select a component type"
                      options={Object.entries(POSITION_COMPONENT_TYPES).map(([key, value]) => {
                        return { label: key, value }
                      })}
                    />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
          <FormFieldRow>
            <FormField
              control={form.control}
              name="total_amount_base"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Total Amount Base</FormLabel>
                      <FormError />
                    </div>
                    <NumberInput
                      thousandSeparator=","
                      value={field.value}
                      onValueChange={(values) => field.onChange(values.floatValue)}
                    />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
          <FormFieldRow>
            <FormField
              name="allocation_rule"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Allocation Rule</FormLabel>
                    <FormError />
                  </div>
                  <SearchSelect
                    disabled
                    value={field.value}
                    setValue={(val) => field.onChange(val)}
                    placeholder="Select an allocation rule"
                    options={Object.entries(ALLOCATION_RULES).map(([key, value]) => {
                      return { label: key, value }
                    })}
                  />
                </FormItem>
              )}
            />
          </FormFieldRow>
          <FormFieldRow>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Reason</FormLabel>
                      <FormError />
                    </div>
                    <FormDescription>Provide a reason for adding this component.</FormDescription>
                    <Textarea value={field.value} onChange={(value) => field.onChange(value)} />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
          <FormFieldRow>
            <FormField
              control={form.control}
              name="source_reference"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Source Reference</FormLabel>
                      <FormError />
                    </div>
                    <FormDescription>
                      Enter any information used as a reference for adding this component.
                    </FormDescription>
                    <Textarea value={field.value ?? ''} onChange={(value) => field.onChange(value)} />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
        </div>
      </Form>
    </ConfirmationDialog>
  )
}
